'use client';

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { loadQueue, subscribeQueue } from '@/utils/offlineQueue';
import type { OfflineOperation } from '@/types/offlineQueue';
import { syncOfflineQueue, type SyncResult } from '@/utils/syncEngine';
import useSafeTranslation from '@/hooks/useSafeTranslation';
import { nowAppIso } from '@/lib/dayjs';

// Offline sync plan:
// - Track queue + network status in a single provider so UI can show badges and trigger sync.
// - Auto-sync when connection is restored and there are pending/failed items.
// - Surface success/failure toasts for manual sync attempts.

type OfflineSyncContextValue = {
  queue: OfflineOperation[];
  online: boolean;
  syncing: boolean;
  lastSyncAt: string | null;
  pendingCount: number;
  failedCount: number;
  syncNow: (options?: { silent?: boolean }) => Promise<SyncResult>;
};

const OfflineSyncContext = createContext<OfflineSyncContextValue | undefined>(undefined);

export function OfflineSyncProvider({ children }: { children: React.ReactNode }) {
  const { tSafe: t } = useSafeTranslation();
  const [queue, setQueue] = useState<OfflineOperation[]>([]);
  const [online, setOnline] = useState<boolean>(
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  const [syncing, setSyncing] = useState(false);
  const [lastSyncAt, setLastSyncAt] = useState<string | null>(null);

  const refreshQueue = useCallback(async () => {
    const next = await loadQueue();
    setQueue(next);
  }, []);

  const syncNow = useCallback(
    async (options?: { silent?: boolean }): Promise<SyncResult> => {
      if (syncing) {
        return { status: 'success', synced: 0, failed: 0, pending: queue.length, errors: [] };
      }

      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        setOnline(false);
        return {
          status: 'offline',
          synced: 0,
          failed: 0,
          pending: queue.length,
          errors: ['offline'],
        };
      }

      setSyncing(true);
      const result = await syncOfflineQueue();
      setSyncing(false);
      setLastSyncAt(nowAppIso());
      await refreshQueue();

      if (!options?.silent) {
        if (result.status === 'success' || result.status === 'disabled') {
          notifications.show({
            color: 'teal',
            title: t('status.syncSuccess') ?? 'Synced',
            message:
              result.status === 'disabled'
                ? (t('status.localOnlyMode') ?? 'Changes saved locally.')
                : (t('status.syncSuccessMessage') ?? 'Offline changes are up to date.'),
          });
        } else if (result.status === 'error') {
          notifications.show({
            color: 'red',
            title: t('status.syncFailed') ?? 'Sync failed',
            message: t('status.syncFailedMessage') ?? 'Some changes could not sync. Try again.',
          });
        }
      }

      return result;
    },
    [queue.length, refreshQueue, syncing, t]
  );

  useEffect(() => {
    refreshQueue();
    const unsub = subscribeQueue(() => {
      refreshQueue();
    });
    return () => {
      if (unsub) {
        unsub();
      }
    };
  }, [refreshQueue]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const handleOnline = () => {
      setOnline(true);
      // Auto-sync only when work is pending
      if (queue.some((op) => op.status === 'pending' || op.status === 'failed')) {
        syncNow({ silent: true });
      }
    };
    const handleOffline = () => setOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [queue, syncNow]);

  const pendingCount = useMemo(
    () => queue.filter((op) => op.status === 'pending' || op.status === 'syncing').length,
    [queue]
  );
  const failedCount = useMemo(() => queue.filter((op) => op.status === 'failed').length, [queue]);

  const value: OfflineSyncContextValue = {
    queue,
    online,
    syncing,
    lastSyncAt,
    pendingCount,
    failedCount,
    syncNow,
  };

  return <OfflineSyncContext.Provider value={value}>{children}</OfflineSyncContext.Provider>;
}

export function useOfflineSync() {
  const ctx = useContext(OfflineSyncContext);
  if (!ctx) {
    throw new Error('useOfflineSync must be used within OfflineSyncProvider');
  }
  return ctx;
}
